import { RefObject } from 'react';
import { IExpertPartner } from '../../../../interfaces/expert-partners/IExpertPartner';
import { getExpertPartnerAgentLabel } from '../constants';

const getPartnerKey = (partner: IExpertPartner, index: number) => {
    return String(partner?._id || partner?.id || `${partner?.agent || ''}-${partner?.companyName || ''}-${index}`);
};

const ExpertPartnersTable = ({
    rows,
    loading,
    loadingMore,
    hasMore,
    scrollContainerRef,
    onScrollEnd,
    onEdit,
    onDelete,
}: {
    rows?: IExpertPartner[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    scrollContainerRef?: RefObject<HTMLDivElement>;
    onScrollEnd?: () => void;
    onEdit?: (row: IExpertPartner) => void;
    onDelete?: (row: IExpertPartner) => void;
}) => {
    return (
        <div className='overflow-x-auto border border-1 rounded-xl dark:border-borderColor-darkMode'>
            <div
                ref={scrollContainerRef}
                onScroll={onScrollEnd}
                className='overflow-y-auto scroll-smooth'
                style={{ maxHeight: '650px' }}
            >
                <table className='min-w-full bg-white dark:bg-black dark:text-white min-h-[220px]'>
                    <thead className='sticky top-0 z-10 bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-white'>
                        <tr>
                            <th className='px-4 py-3 min-w-[140px]'>Logo</th>
                            <th className='px-4 py-3 min-w-[220px]'>Company Name</th>
                            <th className='px-4 py-3 min-w-[140px]'>Agent</th>
                            <th className='px-4 py-3 min-w-[280px]'>Expertise</th>
                            <th className='px-4 py-3 min-w-[180px]'>Contact</th>
                            <th className='px-4 py-3 min-w-[180px]'>Location</th>
                            <th className='px-4 py-3 min-w-[170px]'>Colors</th>
                            <th className='px-4 py-3 min-w-[120px]'>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (!rows || rows.length === 0) &&
                            Array.from({ length: 8 }).map((_, i) => (
                                <tr key={`skeleton-${i}`} className='animate-pulse'>
                                    {Array.from({ length: 8 }).map((__, j) => (
                                        <td key={`cell-${i}-${j}`} className='px-4 py-4'>
                                            <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                        {!loading && (!rows || rows.length === 0) && (
                            <tr>
                                <td colSpan={8} className='px-4 py-8 text-center text-gray-500 dark:text-gray-400'>
                                    No expert partners available
                                </td>
                            </tr>
                        )}

                        {rows && rows.length > 0 && rows.map((row, idx) => (
                            <tr key={getPartnerKey(row, idx)} className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm border-b'>
                                <td className='px-4 py-4'>
                                    {row?.logoUrl ? (
                                        <img
                                            src={row.logoUrl}
                                            alt={row?.companyName || 'Expert partner logo'}
                                            className='h-10 w-10 rounded object-cover border border-gray-200 dark:border-gray-700'
                                        />
                                    ) : (
                                        <span className='text-gray-500 dark:text-gray-400'>--</span>
                                    )}
                                </td>
                                <td className='px-4 py-4 font-medium'>{row?.companyName || '--'}</td>
                                <td className='px-4 py-4'>{getExpertPartnerAgentLabel(row?.agent)}</td>
                                <td className='px-4 py-4'>
                                    <p
                                        title={row?.expertise || '--'}
                                        className='max-w-[280px] whitespace-pre-line break-words overflow-hidden text-ellipsis'
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {row?.expertise || '--'}
                                    </p>
                                </td>
                                <td className='px-4 py-4'>{row?.contactUs || '--'}</td>
                                <td className='px-4 py-4'>{[row?.city, row?.country].filter(Boolean).join(', ') || '--'}</td>
                                <td className='px-4 py-4'>
                                    <div className='flex flex-col gap-1 min-w-[160px]'>
                                        <div className='flex items-center gap-2 text-xs'>
                                            <span className='w-[68px] text-gray-500 dark:text-gray-400'>Background</span>
                                            {row?.backgroundColor ? (
                                                <>
                                                    <span
                                                        className='h-4 w-4 rounded border border-gray-300 dark:border-gray-700'
                                                        style={{ backgroundColor: row.backgroundColor }}
                                                        title={row.backgroundColor}
                                                    />
                                                    <span>{row.backgroundColor}</span>
                                                </>
                                            ) : (
                                                <span className='text-gray-500 dark:text-gray-400'>--</span>
                                            )}
                                        </div>
                                        <div className='flex items-center gap-2 text-xs'>
                                            <span className='w-[68px] text-gray-500 dark:text-gray-400'>Border</span>
                                            {row?.borderColor ? (
                                                <>
                                                    <span
                                                        className='h-4 w-4 rounded border-2'
                                                        style={{ borderColor: row.borderColor }}
                                                        title={row.borderColor}
                                                    />
                                                    <span>{row.borderColor}</span>
                                                </>
                                            ) : (
                                                <span className='text-gray-500 dark:text-gray-400'>--</span>
                                            )}
                                        </div>
                                        <div className='flex items-center gap-2 text-xs'>
                                            <span className='w-[68px] text-gray-500 dark:text-gray-400'>Text</span>
                                            {row?.textColor ? (
                                                <>
                                                    <span
                                                        className='h-4 w-4 rounded border border-gray-300 dark:border-gray-700'
                                                        style={{ backgroundColor: row.textColor }}
                                                        title={row.textColor}
                                                    />
                                                    <span>{row.textColor}</span>
                                                </>
                                            ) : (
                                                <span className='text-gray-500 dark:text-gray-400'>--</span>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <div className='flex items-center gap-2'>
                                        <button
                                            type='button'
                                            onClick={() => onEdit?.(row)}
                                            className='px-3 py-1 rounded border border-gray-300 dark:border-borderColor-darkMode hover:bg-gray-100 dark:hover:bg-black'
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type='button'
                                            onClick={() => onDelete?.(row)}
                                            className='px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950'
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}

                        {loadingMore && rows && rows.length > 0 && (
                            <tr>
                                <td colSpan={8} className='px-4 py-5'>
                                    <div className='flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400'>
                                        <svg className='h-5 w-5 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none'>
                                            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
                                        </svg>
                                        <span>Loading more partners...</span>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && !loadingMore && rows && rows.length > 0 && hasMore === false && (
                            <tr>
                                <td colSpan={8} className='px-4 py-4 text-center text-xs text-gray-400'>
                                    All expert partners loaded
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ExpertPartnersTable;
